import { Box, Button, Field, Input, Flex, HStack, Center, VStack } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import {
  ButtonGray,
  SearchInput,
  UserSelectorDialog,
  tools,
  Avatar,
  UploadAvatar,
  CameraIcon,
} from '@halo/ui-chat-react';
import type { MessagesStore, Msg, SessionStoreInstance } from '@halo/chat-sdk-react';
import { useAppStore, type User } from '@halo/chat-sdk-react';
import type { RefObject } from 'react';
import { ForwardComposerPreview } from '../ForwardToDialog/ForwardComposerPreview';
import {
  ForwardComposerInput,
  type ForwardComposerInputRef,
} from '../ForwardToDialog/ForwardComposerFooter';
import { useSearch } from './helpers';
import { Accord } from './Accord';

export interface GroupCreateDialogProps {
  checkUserKey?: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  sessionStoreInstance: SessionStoreInstance | null;
  onOK: (
    name: string,
    selected: (User & { isGroup: boolean })[],
    forwardData?: {
      forwardMsg?: Msg;
      forwardMsgUser?: User;
      refComponent?: RefObject<ForwardComposerInputRef | null>;
    },
    avatar?: File | null
  ) => Promise<void>;
  onClose?: () => void;
  getInitialGroupCreateSelectedUsers?: () => User[];
}

function GroupCreateDialog({
  isOpen,
  setIsOpen,
  sessionStoreInstance,
  onOK,
  checkUserKey = true,
  getInitialGroupCreateSelectedUsers,
}: GroupCreateDialogProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useAppStore();
  const [groupName, setGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [resetTrigger, setResetTrigger] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [draftMessage, setDraftMessage] = useState<MessagesStore['draftMessage']>();

  const { list } = useSearch(searchValue, resetTrigger, isOpen);

  const forwardComposerRef = useRef<ForwardComposerInputRef>(null);

  const forwardMsg = useMemo(() => {
    return draftMessage?.forwardMsg;
  }, [draftMessage?.forwardMsg]);

  const forwardMsgUser = useMemo(() => {
    return draftMessage?.forwardMsgUser;
  }, [draftMessage?.forwardMsgUser]);

  const listFormatted = useMemo(() => {
    return list.map(({ items, label, key }) => {
      const filteredItems = items?.map((item) => {
        const isMe = item.user_id === currentUser?.user_id;
        return isMe ? { ...item, defaultChecked: true, disabled: true } : item;
      });
      return { label, items: filteredItems, key };
    });
  }, [list, currentUser?.user_id]);

  const { selected, toggleSelectUser, setSelected, onRemove, resetDialogState } =
    UserSelectorDialog.useUserSelector(listFormatted, checkUserKey);

  useEffect(() => {
    if (!isOpen) return;
    const users = getInitialGroupCreateSelectedUsers?.();
    if (!users?.length) return;
    setSelected(users.map((user) => ({ ...user, defaultChecked: true })));
  }, [isOpen, listFormatted, setSelected, getInitialGroupCreateSelectedUsers]);

  const resetForwardState = useCallback(() => {
    const sessionStore = sessionStoreInstance?.getState();
    sessionStore?.setDraftMessage?.({
      forwardId: undefined,
      forwardMsg: undefined,
      forwardMsgUser: undefined,
    });
  }, [sessionStoreInstance]);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarSrc, setAvatarSrc] = useState('');

  const handleAvatarUpdate = useCallback(async (file: File) => {
    setAvatarFile(file);
    setAvatarSrc(URL.createObjectURL(file));
  }, []);
  const { run } = useDebounceFn(
    (value) => {
      setSearchValue(value);
    },
    {
      wait: 500,
    }
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
      setGroupName('');
      setResetTrigger((prev) => prev + 1);
      resetForwardState();
      setAvatarFile(null);
      setAvatarSrc('');
    }
  }, [isOpen, resetDialogState, resetForwardState]);

  useEffect(() => {
    if (sessionStoreInstance) {
      return sessionStoreInstance.subscribe((state) => {
        setDraftMessage(state.draftMessage);
      });
    }
  }, [sessionStoreInstance]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      placement="center"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content borderRadius="xl" w="800px" maxW="100%" h="calc(100vh - 48px)">
          <Dialog.Header pt="5">
            <Dialog.Title fontWeight="semibold">
              {forwardMsg ? t('chat.createGroupAndForward') : t('chat.createGroup')}
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body overflow="hidden">
            <VStack gap="6" w="full" h="full" css={{ '--field-label-width': '140px' }}>
              <HStack w="full" gap="6">
                <Center>
                  <UploadAvatar onChange={handleAvatarUpdate}>
                    <Box
                      pos="relative"
                      cursor="pointer"
                      css={{
                        '&:hover': {
                          '--show-update-avatar': 'flex',
                        },
                      }}
                    >
                      <Avatar
                        css={{
                          outlineWidth: '3px',
                          outlineColor: 'bg',
                          outlineStyle: 'solid',
                        }}
                        src={avatarSrc}
                        w={16}
                        height={16}
                      />
                      <Flex
                        pos="absolute"
                        top={0}
                        left={0}
                        alignItems="center"
                        justifyContent="center"
                        zIndex={10}
                        w={16}
                        height={16}
                        bg={avatarSrc ? 'blackAlpha.70' : 'brand.50'}
                        rounded="50%"
                        display={avatarSrc ? 'var(--show-update-avatar, none)' : 'flex'}
                        transition="scale .3s"
                        _hover={{
                          scale: avatarSrc ? 1 : 1.2,
                        }}
                      >
                        <CameraIcon width="24px" height="24px" />
                      </Flex>
                    </Box>
                  </UploadAvatar>
                </Center>
                <Field.Root orientation="horizontal" alignItems="self-start">
                  <Input
                    maxLength={32}
                    flex="1"
                    size="sm"
                    placeholder={t('chat.createGroupNamePlaceholder')}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                </Field.Root>
              </HStack>
              <Field.Root flex="1" overflow="hidden">
                <VStack w="full" h="full" gap="4">
                  <Field.Label w="full">{t('chat.createGroupMembers')}</Field.Label>
                  <Box flex="1" w="full" overflow="hidden">
                    <Flex
                      border="1px solid"
                      borderColor="border"
                      borderRadius="lg"
                      alignItems="stretch"
                      gap="0"
                      minW="0"
                      h="full"
                      overflow="hidden"
                    >
                      <VStack
                        flex="1"
                        p="3"
                        borderRight="1px solid"
                        borderColor="border"
                        minW="0"
                        h="full"
                        overflow="hidden"
                      >
                        <SearchInput value={searchValue} onChange={run} />
                        <Box flex="1" w="full" overflowY="auto">
                          <Accord
                            list={listFormatted}
                            toggleSelectUser={toggleSelectUser}
                            selected={selected}
                            checkUserKey={checkUserKey}
                          />
                        </Box>

                        {/* {listFlat.length > 0 && canSelectList.length > 0 && (
                          <UserSelectorDialog.SelectAll
                            isChecked={isSelectedAll}
                            onChange={onSelectAllChange}
                          />
                        )} */}
                      </VStack>
                      <Box flex="1" minW="0" h="full" overflow="auto">
                        <UserSelectorDialog.SelectedList
                          selected={selected}
                          onRemove={onRemove}
                          checkUserKey={checkUserKey}
                          extraElement={
                            forwardMsg && (
                              <ForwardComposerPreview
                                message={forwardMsg}
                                bg="bg.subtle"
                                borderRadius="md"
                              />
                            )
                          }
                          height="calc(100% - 1em)"
                        />
                      </Box>
                    </Flex>
                    {forwardMsg && (
                      <ForwardComposerInput
                        refComponent={forwardComposerRef}
                        mt="2"
                        borderRadius="md"
                      />
                    )}
                  </Box>
                </VStack>
              </Field.Root>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.ActionTrigger asChild>
              <ButtonGray h="10" borderRadius="lg">
                {t('buttons.cancel')}
              </ButtonGray>
            </Dialog.ActionTrigger>
            <Button
              h="10"
              borderRadius="lg"
              disabled={isLoading}
              onClick={() => {
                const forwardData = forwardMsg
                  ? { forwardMsg: forwardMsg, forwardMsgUser, refComponent: forwardComposerRef }
                  : undefined;
                if (forwardMsg && !forwardMsg) {
                  tools.toaster.error({
                    title: t('tips.messageDeleted'),
                  });
                  return;
                }
                setIsLoading(true);
                onOK(groupName.trim(), selected, forwardData, avatarFile).finally(() => {
                  setIsLoading(false);
                });
              }}
            >
              {forwardMsg ? t('chat.createGroupAndForward') : t('buttons.create')}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

const MemoizedGroupCreateDialog = memo(GroupCreateDialog);

export { MemoizedGroupCreateDialog as GroupCreateDialog };
